import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BookOpen, Send, Heart, Loader2, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { getGuestbookMessages, addGuestbookMessage } from '@/lib/api';

const GuestbookCard = ({ message, index }) => {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short'
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.1 }}
      className="bg-bone rounded-xl p-4 shadow-sm border border-gold/20 hover:shadow-md transition-shadow"
    >
      <p className="text-charcoal text-sm leading-relaxed mb-3 italic">
        "{message.message}"
      </p>
      <div className="flex items-center justify-between">
        <span className="text-terracotta font-medium text-sm">{message.name}</span>
        <span className="text-stone text-xs">{formatDate(message.created_at)}</span>
      </div>
    </motion.div>
  );
};

const Guestbook = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({ name: '', message: '' });

  useEffect(() => {
    loadMessages();
  }, []);

  const loadMessages = async () => {
    try {
      const response = await getGuestbookMessages();
      setMessages(response.data || []);
    } catch (error) {
      console.error('Error loading guestbook:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name.trim() || !formData.message.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setSubmitting(true);
    try {
      const response = await addGuestbookMessage(formData);
      toast.success(response.data.message || 'Message envoyé ! Il sera visible après validation.');
      setFormData({ name: '', message: '' });
      setShowForm(false);
      loadMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="bg-white/90 backdrop-blur-md rounded-2xl p-5 border border-gold/30 shadow-lg">
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
          <BookOpen className="w-5 h-5 text-gold" />
        </div>
        <div>
          <h3 className="font-display text-xl text-charcoal">Juste un mot...</h3>
          <p className="text-xs text-stone">avant le mariage</p>
        </div>
      </div>

      {/* Instructions */}
      <div className="bg-bone rounded-lg p-3 mb-4 text-sm text-charcoal">
        <p className="flex items-start gap-2">
          <Heart className="w-4 h-4 text-terracotta mt-0.5 flex-shrink-0" />
          <span>
            Partagez vos vœux, un souvenir, ou simplement un mot d'encouragement 
            pour les futurs mariés. Vos messages seront visibles par tous les invités !
          </span>
        </p>
      </div>

      {/* Messages List */}
      <div className="space-y-3 mb-4 max-h-80 overflow-y-auto pr-1">
        {loading ? (
          <div className="flex justify-center py-6">
            <Loader2 className="w-6 h-6 animate-spin text-gold" />
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center py-6 text-stone">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Soyez le premier à laisser un message !</p>
          </div>
        ) : (
          messages.slice(0, 5).map((msg, index) => (
            <GuestbookCard key={msg.id} message={msg} index={index} />
          ))
        )}
        
        {messages.length > 5 && (
          <p className="text-center text-xs text-stone pt-2">
            + {messages.length - 5} autres messages
          </p>
        )}
      </div>

      {/* Add Message Form */}
      <AnimatePresence>
        {showForm ? (
          <motion.form
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            onSubmit={handleSubmit}
            className="space-y-3 overflow-hidden"
          >
            <Input
              placeholder="Votre prénom"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="bg-bone border-gold/30 text-charcoal placeholder:text-stone"
              maxLength={50}
            />
            <Textarea
              placeholder="Votre message (max 500 caractères)"
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="bg-bone border-gold/30 text-charcoal placeholder:text-stone resize-none"
              rows={3}
              maxLength={500}
            />
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowForm(false)}
                className="flex-1 border-stone/30 text-charcoal"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                size="sm"
                disabled={submitting}
                className="flex-1 bg-gold hover:bg-gold/90 text-white"
              >
                {submitting ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-1" />
                    Envoyer
                  </>
                )}
              </Button>
            </div>
          </motion.form>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Button
              onClick={() => setShowForm(true)}
              className="w-full bg-gold hover:bg-gold/90 text-white"
              data-testid="guestbook-add-btn"
            >
              <BookOpen className="w-4 h-4 mr-2" />
              Écrire un petit mot
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Guestbook;
